/**
 * shipping-calculator-v2 — pre-checkout shipping estimator.
 *
 * Fetches live rates for the current cart from the Shopify AJAX API,
 * shows the cheapest rate as a headline, flags free shipping, nudges
 * toward the configured free-shipping threshold, and reveals a
 * proceed-to-checkout CTA once rates are on screen.
 *
 * Rendered by snippets/shipping-calculator-v2.liquid.
 */
if (!customElements.get('shipping-calculator-v2')) {
  class ShippingCalculatorV2 extends HTMLElement {
    constructor() {
      super();
      this.zip = this.querySelector('.js-zip-input');
      this.button = this.querySelector('.js-calc-submit');
      this.zip.addEventListener('keydown', this.handleZipKeydown.bind(this));
      this.button.addEventListener('click', this.handleSubmit.bind(this));
    }

    connectedCallback() {
      this.country = this.querySelector('.js-country-select');
      this.province = this.querySelector('.js-province-select');
      this.provinceContainer = this.querySelector('.js-province-container');

      this.restoreSavedAddress();
      this.applyDefaultCountry();
      this.initCollapse();

      if (this.province && this.provinceContainer) {
        this.handleCountryChange();
        this.country.addEventListener('change', this.handleCountryChange.bind(this));
      }
    }

    init() {
      this.result = this.querySelector('.js-result');
      this.headline = this.querySelector('.js-headline');
      this.ratesList = this.querySelector('.js-rates-list');
      this.toggleRatesBtn = this.querySelector('.js-toggle-rates');
      this.nudge = this.querySelector('.js-nudge');
      this.cta = this.querySelector('.js-cta');
      this.errors = this.querySelector('.js-errors');

      if (this.toggleRatesBtn) {
        this.toggleRatesBtn.addEventListener('click', () => {
          Array.from(this.ratesList.children).forEach((item) => {
            item.hidden = false;
          });
          this.toggleRatesBtn.hidden = true;
        });
      }

      this.initialised = true;
    }

    /**
     * Wires the drawer's collapse toggle. The header stays visible while the
     * body folds away, and the choice is kept in sessionStorage so the drawer
     * re-rendering after a cart update doesn't reopen a panel the buyer closed.
     *
     * connectedCallback runs on every connection, not just the first, so the
     * listener is bound once per element. Binding it twice would toggle twice
     * per tap and leave the panel exactly where it started — a toggle that
     * looks broken rather than one that visibly misbehaves.
     */
    initCollapse() {
      this.collapseBtn = this.querySelector('.js-collapse');
      this.body = this.querySelector('.js-body');
      if (!this.collapseBtn || !this.body) return;

      let collapsed = false;
      try {
        collapsed = sessionStorage.getItem(ShippingCalculatorV2.collapseKey) === 'true';
      } catch (e) {
        collapsed = false;
      }

      this.setCollapsed(collapsed);

      if (this.collapseBound) return;
      this.collapseBound = true;
      this.collapseBtn.addEventListener('click', () => this.setCollapsed(!this.collapsed));
    }

    /**
     * Shows or hides the calculator body and syncs the toggle's state.
     * @param {boolean} collapsed - True to fold the body away.
     */
    setCollapsed(collapsed) {
      this.collapsed = collapsed;
      this.body.hidden = collapsed;
      this.classList.toggle('ship-calc--collapsed', collapsed);
      this.collapseBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');

      try {
        sessionStorage.setItem(ShippingCalculatorV2.collapseKey, collapsed ? 'true' : 'false');
      } catch (e) {
        // Private browsing — the toggle still works, it just won't be remembered.
      }
    }

    /**
     * Prefills country/zip from the buyer's last estimate.
     */
    restoreSavedAddress() {
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem('eno-shipping-estimate') || 'null');
      } catch (e) {
        saved = null;
      }
      if (!saved) return;

      const country = this.querySelector('.js-country-select');
      if (saved.country && country && !country.dataset.default) {
        country.dataset.default = saved.country;
      }
      if (saved.zip) this.zip.value = saved.zip;
    }

    /**
     * Preselects the country <select> option matching its `data-default`
     * value (customer's default address country, or the section setting).
     */
    applyDefaultCountry() {
      if (!this.country || !this.country.dataset.default) return;
      ShippingCalculatorV2.setSelectedOption(this.country, this.country.dataset.default);
    }

    /**
     * Repopulates the province <select> for the currently selected country,
     * hiding the province field entirely for countries that don't have one.
     */
    handleCountryChange() {
      const selectedOption = this.country.options[this.country.selectedIndex];
      let provinces = [];
      try {
        provinces = JSON.parse(selectedOption.dataset.provinces || '[]');
      } catch (e) {
        provinces = [];
      }

      Array.from(this.province.options).forEach((option) => option.remove());

      if (provinces.length === 0) {
        this.provinceContainer.hidden = true;
        return;
      }

      provinces.forEach((province) => {
        const option = document.createElement('option');
        [option.value, option.innerHTML] = province;
        this.province.appendChild(option);
      });

      if (this.province.dataset.default) {
        ShippingCalculatorV2.setSelectedOption(this.province, this.province.dataset.default);
      }

      this.provinceContainer.hidden = false;
    }

    /**
     * Sets the selected option of a <select> element by value or label.
     * @param {Element} selector - Select element.
     * @param {string} value - Value (or label) of the option to select.
     */
    static setSelectedOption(selector, value) {
      Array.from(selector.options).forEach((option, index) => {
        if (option.value === value || option.innerHTML === value) {
          selector.selectedIndex = index;
        }
      });
    }

    /**
     * Persists the address used for the estimate.
     */
    saveAddress() {
      try {
        localStorage.setItem(
          'eno-shipping-estimate',
          JSON.stringify({ country: this.country.value, zip: this.zip.value })
        );
      } catch (e) {
        // Storage unavailable (private mode) — non-critical.
      }
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      if (!this.initialised) this.init();

      this.errors.hidden = true;
      this.result.hidden = true;
      this.button.disabled = true;
      this.button.classList.add('is-loading');

      const province = this.province ? this.province.value : '';
      const params = `shipping_address[zip]=${encodeURIComponent(this.zip.value)}&shipping_address[country]=${encodeURIComponent(this.country.value)}&shipping_address[province]=${encodeURIComponent(province)}`;

      try {
        const response = await fetch(`${theme.routes.cart}/shipping_rates.json?${params}`);
        const data = await response.json();

        if (response.ok) {
          this.showRates(data);
          this.saveAddress();
          this.dispatchResultEvent(data);
        } else {
          this.showErrors(data);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
      } finally {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }

    handleZipKeydown(evt) {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        this.handleSubmit(evt);
      }
    }

    /**
     * Broadcasts the estimate for analytics listeners.
     * @param {object} data - Rates response data.
     */
    dispatchResultEvent(data) {
      document.dispatchEvent(
        new CustomEvent('on:shipping-calculator:result', {
          detail: {
            country: this.country.value,
            zip: this.zip.value,
            rates: data.shipping_rates || []
          }
        })
      );
    }

    /**
     * Formats a money string using the store money format.
     * @param {string|number} amount - Amount as a decimal string or number.
     * @returns {string} - Formatted price.
     */
    static formatMoney(amount) {
      return theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, amount);
    }

    /**
     * sessionStorage key holding the drawer toggle's open/closed state.
     */
    static get collapseKey() {
      return 'eno-shipping-calc-collapsed';
    }

    /**
     * Number of rates shown before the "View more" toggle is needed.
     */
    static get visibleRatesCount() {
      return 3;
    }

    /**
     * Resolves the free-shipping threshold (in cents) for the selected
     * country, from the settings.free_shipping_minimum config. Lines are
     * either "CUR:AMT" (all countries using CUR) or "CC:CUR:AMT"
     * (country-specific, listed first).
     * @returns {number|null} - Threshold in cents, or null if none applies.
     */
    getFreeShippingThreshold() {
      const config = (this.dataset.freeShippingConfig || '').trim();
      if (!config) return null;

      const currency = this.dataset.currency;
      const selectedCountry = this.country.value;
      let regionNames = null;
      try {
        regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
      } catch (e) {
        regionNames = null;
      }

      const lines = config.split('\n').map((line) => line.trim()).filter(Boolean);

      for (let i = 0; i < lines.length; i += 1) {
        const parts = lines[i].split(':');
        if (parts.length === 3 && parts[1] === currency && regionNames) {
          if (regionNames.of(parts[0].toUpperCase()) === selectedCountry) {
            return parseFloat(parts[2]) * 100;
          }
        } else if (parts.length === 2 && parts[0] === currency) {
          return parseFloat(parts[1]) * 100;
        }
      }

      return null;
    }

    /**
     * Renders the estimate: cheapest-rate headline, free-shipping state,
     * threshold nudge, full rate list, and the checkout CTA.
     * @param {object} data - Rates response data.
     */
    showRates(data) {
      const rates = data.shipping_rates || [];
      this.ratesList.innerHTML = '';
      this.ratesList.hidden = true;
      this.toggleRatesBtn.hidden = true;
      this.nudge.hidden = true;
      if (this.cta) this.cta.hidden = true;
      this.classList.remove('ship-calc--free');

      if (rates.length === 0) {
        this.headline.textContent = theme.strings.shippingCalculator.noRates;
        this.result.hidden = false;
        return;
      }

      const cheapest = rates.reduce((min, rate) => (
        parseFloat(rate.price) < parseFloat(min.price) ? rate : min
      ), rates[0]);

      if (parseFloat(cheapest.price) === 0) {
        this.classList.add('ship-calc--free');
        this.headline.textContent = this.dataset.textFree;
      } else {
        this.headline.textContent = `${this.dataset.textEstimate} ${ShippingCalculatorV2.formatMoney(cheapest.price)}`;
        this.showNudge();
      }

      if (rates.length > 1) {
        rates.forEach((rate, index) => {
          const item = document.createElement('li');
          item.textContent = `${rate.name}: ${ShippingCalculatorV2.formatMoney(rate.price)}`;
          item.hidden = index >= ShippingCalculatorV2.visibleRatesCount;
          this.ratesList.appendChild(item);
        });
        this.ratesList.hidden = false;
        this.toggleRatesBtn.hidden = rates.length <= ShippingCalculatorV2.visibleRatesCount;
      }

      if (this.cta) this.cta.hidden = false;
      this.result.hidden = false;
    }

    /**
     * Shows how far the cart is from the free-shipping threshold, when one
     * is configured for the selected country and not yet met.
     */
    showNudge() {
      const threshold = this.getFreeShippingThreshold();
      const cartTotal = parseInt(this.dataset.cartTotal, 10);

      if (threshold === null || Number.isNaN(cartTotal) || cartTotal >= threshold) return;

      const remaining = ((threshold - cartTotal) / 100).toFixed(2);
      this.nudge.textContent = this.dataset.textNudge.replace('[amount]', ShippingCalculatorV2.formatMoney(remaining));
      this.nudge.hidden = false;
    }

    /**
     * Formats and shows errors relating to rates retrieval.
     * @param {object} data - Response data.
     */
    showErrors(data) {
      let errors = '';

      Object.keys(data).forEach((key) => {
        errors += `<li>${data[key]}</li>`;
      });

      this.errors.querySelector('.js-errors-text').innerHTML = `<ul class="styled-list">${errors}</ul>`;
      this.errors.hidden = false;
    }
  }

  customElements.define('shipping-calculator-v2', ShippingCalculatorV2);
}
